import React from 'react';

import css from './ListingPolicySections.module.css';

const UTV_CHECKLIST_DOWNLOAD_URL = '/static/docs/utv-renter-safety-checklist.pdf';


const formatSecurityDepositText = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const rawValue =
    typeof value === 'object' && value.amount != null
      ? value.amount
      : value;

  const numericValue =
    typeof rawValue === 'number'
      ? rawValue
      : Number(String(rawValue).replace(/[^0-9.]/g, ''));

  if (Number.isFinite(numericValue) && numericValue > 0) {
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(numericValue);

    return `Refundable security deposit: ${formattedAmount}. This amount may be held or collected before pickup and released after the rental is returned without damage, missing equipment, or late-return issues.`;
  }

  const fallbackText = formatTextValue(value);
  return fallbackText
    ? `Refundable security deposit: ${fallbackText}`
    : null;
};


const isUtvRental = publicData => {
  const searchable = JSON.stringify(publicData || {}).toLowerCase();
  return /\butv\b|\butvs\b|side[\s-]?by[\s-]?side|\bsxs\b/.test(searchable);
};

const UTVChecklistDownloadCard = props => {
  const { publicData } = props;

  if (!isUtvRental(publicData)) {
    return null;
  }

  return (
    <section className={css.utvChecklistSection}>
      <div>
        <p className={css.utvChecklistEyebrow}>UTV rental checklist</p>
        <h2>Download the renter safety checklist</h2>
        <p>
          Review this checklist before pickup so safety gear, vehicle inspection,
          owner instructions, emergency procedures, and return expectations are clear.
        </p>
      </div>

      <a className={css.utvChecklistButton} href={UTV_CHECKLIST_DOWNLOAD_URL} download>
        Download checklist
      </a>
    </section>
  );
};



const ThrottleXVisiblePolicySummary = () => (
  <section className={css.visiblePolicySummary}>
    <div className={css.visiblePolicyHeader}>
      <span className={css.visiblePolicyBadge}>Website policies</span>
      <h3 className={css.visiblePolicyTitle}>Important ThrottleX rental policies</h3>
      <p className={css.visiblePolicyIntro}>
        These are the key marketplace rules renters should know before sending a booking request.
      </p>
    </div>

    <div className={css.visiblePolicyList}>
      <div className={css.visiblePolicyItem}>
        <strong>Booking must be confirmed</strong>
        <p>
          A request is not final until the booking is accepted and processed through ThrottleX.
        </p>
      </div>

      <div className={css.visiblePolicyItem}>
        <strong>Pickup privacy</strong>
        <p>
          Exact pickup details are shared only after the booking is confirmed.
        </p>
      </div>

      <div className={css.visiblePolicyItem}>
        <strong>Renter responsibility</strong>
        <p>
          Renters are responsible for safe use, on-time return, damage, fines, tickets, tolls,
          fuel, cleaning, and misuse.
        </p>
      </div>

      <div className={css.visiblePolicyItem}>
        <strong>Platform fees</strong>
        <p>
          ThrottleX may apply renter service fees and deduct an owner commission from completed bookings.
        </p>
      </div>
    </div>

    <a href="/p/terms" className={css.visiblePolicyLink}>
      View full Terms of Service
    </a>
  </section>
);


const FALLBACKS = {
  amenities:
    'The owner has not listed specific amenities or included equipment yet.',
  cancellation:
    'Cancellation terms have not been added by the owner yet. Review the final terms before payment.',
  deposit:
    'No refundable security deposit has been added by the owner yet.',
  operator:
    'Captain and operator requirements have not been added yet. Confirm all operating requirements with the owner.',
};

const normalizeKey = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const FIELD_ALIASES = {
  amenities: [
    'amenities',
    'amenity',
    'rentalAmenities',
  ],

  includedEquipment: [
    'includedEquipment',
    'included-equipment',
    'included_equipment',
    'equipmentIncluded',
    'equipment-included',
    'rentalIncludes',
    'includedGear',
    'includedItems',
  ],

  cancellation: [
    'cancellationPolicy',
    'cancellation-policy',
    'cancellation_policy',
    'cancelPolicy',
    'cancel-policy',
    'cancellation',
    'cancellationTerms',
    'refundPolicy',
  ],

  deposit: [
    'securityDeposit',
    'security-deposit',
    'security_deposit',
    'securityDepositAmount',
    'depositRequired',
    'damageDeposit',
    'depositPolicy',
    'deposit',
  ],

  operator: [
    'captainOperator',
    'captain-operator',
    'captain_operator',
    'captainOperatorInfo',
    'captainOperatorRequirements',
    'operatorRequirements',
    'operator-requirements',
    'operatorInfo',
    'captainInfo',
    'captainOption',
    'driverOption',
    'driverInfo',
  ],
};

const fieldsShownInCards = [
  ...FIELD_ALIASES.amenities,
  ...FIELD_ALIASES.includedEquipment,
  ...FIELD_ALIASES.cancellation,
  ...FIELD_ALIASES.deposit,
  ...FIELD_ALIASES.operator,
  'minimumRenterAge',
  'minimum-renter-age',
  'minimumAge',
  'renterAge',
  'requiredLicenses',
  'requiredLicense',
  'requiredCertifications',
  'licenseRequirements',
].map(normalizeKey);

export const isListingPolicyField = key =>
  fieldsShownInCards.includes(normalizeKey(key));

const hasValue = value => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const humanizeOption = value =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, character => character.toUpperCase());

const findValue = (data, aliases) => {
  const normalizedData = Object.entries(data || {}).reduce(
    (result, [key, value]) => {
      result[normalizeKey(key)] = value;
      return result;
    },
    {}
  );

  for (const alias of aliases) {
    const value = normalizedData[normalizeKey(alias)];

    if (hasValue(value)) {
      return value;
    }
  }

  return null;
};

const formatTextValue = value => {
  if (!hasValue(value)) {
    return null;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map(item => formatTextValue(item))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'object') {
    if (hasValue(value.label)) {
      return String(value.label).trim();
    }

    if (hasValue(value.name)) {
      return String(value.name).trim();
    }

    if (hasValue(value.title)) {
      return String(value.title).trim();
    }

    return Object.entries(value)
      .map(([key, itemValue]) => {
        const formattedValue = formatTextValue(itemValue);

        return formattedValue
          ? `${humanizeOption(key)}: ${formattedValue}`
          : null;
      })
      .filter(Boolean)
      .join(' • ');
  }

  const stringValue = String(value).trim();

  const looksLikeOptionId =
    !stringValue.includes(' ') &&
    (stringValue.includes('_') || stringValue.includes('-'));

  return looksLikeOptionId
    ? humanizeOption(stringValue)
    : stringValue;
};

const normalizeList = value => {
  if (!hasValue(value)) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .flatMap(item => normalizeList(item))
      .filter(Boolean);
  }

  if (typeof value === 'object') {
    if (hasValue(value.label) || hasValue(value.name) || hasValue(value.title)) {
      return [formatTextValue(value)].filter(Boolean);
    }

    return Object.entries(value)
      .filter(([, itemValue]) => itemValue === true || hasValue(itemValue))
      .map(([key, itemValue]) => {
        if (itemValue === true) {
          return humanizeOption(key);
        }

        return formatTextValue(itemValue);
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/[\n,;]+/)
      .map(item => {
        const trimmedItem = item.trim();

        if (!trimmedItem) {
          return null;
        }

        const looksLikeOptionId =
          !trimmedItem.includes(' ') &&
          (trimmedItem.includes('_') || trimmedItem.includes('-'));

        return looksLikeOptionId
          ? humanizeOption(trimmedItem)
          : trimmedItem;
      })
      .filter(Boolean);
  }

  return [formatTextValue(value)].filter(Boolean);
};

const uniqueItems = items =>
  items.filter(
    (item, index, collection) =>
      collection.findIndex(
        existingItem =>
          existingItem.toLowerCase() === item.toLowerCase()
      ) === index
  );

const getOperatorText = data => {
  const operatorValue = findValue(data, FIELD_ALIASES.operator);

  if (hasValue(operatorValue)) {
    return formatTextValue(operatorValue);
  }

  const captainRequired = findValue(data, [
    'captainRequired',
    'captain-required',
  ]);

  if (typeof captainRequired === 'boolean') {
    return captainRequired
      ? 'A captain or qualified operator is required for this rental.'
      : 'A captain is not required for this rental.';
  }

  const captainIncluded = findValue(data, [
    'captainIncluded',
    'captain-included',
    'captainProvided',
  ]);

  if (typeof captainIncluded === 'boolean') {
    return captainIncluded
      ? 'A captain or operator is included with this rental.'
      : 'A captain or operator is not included with this rental.';
  }

  return null;
};

const DetailIcon = props => {
  const { type } = props;

  if (type === 'amenities') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7" rx="2" />
        <rect x="14" y="3" width="7" height="7" rx="2" />
        <rect x="3" y="14" width="7" height="7" rx="2" />
        <rect x="14" y="14" width="7" height="7" rx="2" />
      </svg>
    );
  }

  if (type === 'cancellation') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M8 3v4M16 3v4M3 10h18M9 14l6 6M15 14l-6 6" />
      </svg>
    );
  }

  if (type === 'deposit') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3l8 3v5c0 5-3.2 8.6-8 10-4.8-1.4-8-5-8-10V6l8-3z" />
        <path d="M12 7v10M15 9.5c-.7-.8-1.7-1.2-3-1.2-1.7 0-2.8.8-2.8 2 0 3 5.6 1.4 5.6 4.4 0 1.2-1.1 2.1-2.9 2.1-1.4 0-2.5-.5-3.2-1.4" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c.8-4.6 3.4-7 8-7s7.2 2.4 8 7" />
      <path d="M18 6l2 2 3-4" />
    </svg>
  );
};

const DetailCard = props => {
  const { type, title, children, fallback } = props;

  const cardClassName = [css.card, css[type]]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={cardClassName}>
      <div className={css.cardHeader}>
        <span className={css.iconWrapper}>
          <DetailIcon type={type} />
        </span>

        <h3 className={css.cardTitle}>{title}</h3>
      </div>

      <div className={fallback ? css.fallbackText : css.cardContent}>
        {children}
      </div>
    </article>
  );
};

const ListingPolicySections = props => {
  const {
    publicData = {},
    metadata = {},
  } = props;

  /*
   * Custom listing fields can come from either public data or metadata.
   * Public data takes priority if the same key exists in both.
   */
  const listingData = {
    ...metadata,
    ...publicData,
  };

  const amenitiesValue = findValue(
    listingData,
    FIELD_ALIASES.amenities
  );

  const includedEquipmentValue = findValue(
    listingData,
    FIELD_ALIASES.includedEquipment
  );

  const amenities = uniqueItems([
    ...normalizeList(amenitiesValue),
    ...normalizeList(includedEquipmentValue),
  ]);

  const cancellationValue = findValue(
    listingData,
    FIELD_ALIASES.cancellation
  );

  const depositValue = findValue(
    listingData,
    FIELD_ALIASES.deposit
  );

  const cancellationText = formatTextValue(cancellationValue);
  const depositText = formatSecurityDepositText(depositValue);
  const operatorText = getOperatorText(listingData);

  const minimumAgeValue = findValue(listingData, [
    'minimumRenterAge',
    'minimum-renter-age',
    'minimumAge',
    'renterAge',
  ]);

  const licensesValue = findValue(listingData, [
    'requiredLicenses',
    'requiredLicense',
    'requiredCertifications',
    'licenseRequirements',
  ]);

  const minimumAgeText = formatTextValue(minimumAgeValue);
  const requiredLicenses = normalizeList(licensesValue);

  const operatorDetails = [
    operatorText,
    minimumAgeText
      ? `Minimum renter age: ${minimumAgeText}`
      : null,
    requiredLicenses.length > 0
      ? `Required licenses or certifications: ${requiredLicenses.join(', ')}`
      : null,
  ].filter(Boolean);

  return (
    <section
      className={css.root}
      aria-labelledby="rental-details-heading"
    >
      <div className={css.sectionHeading}>
        <span className={css.eyebrow}>Before you book</span>

        <h2
          id="rental-details-heading"
          className={css.title}
        >
          Rental details
        </h2>

        <p className={css.intro}>
          Review what is included and any important requirements for this rental.
        </p>
      </div>

      
<div className={css.grid}>
        <DetailCard
          type="amenities"
          title="Amenities"
          fallback={amenities.length === 0}
        >
          {amenities.length > 0 ? (
            <ul className={css.amenitiesList}>
              {amenities.map((amenity, index) => (
                <li
                  className={css.amenity}
                  key={`${amenity}-${index}`}
                >
                  <span className={css.checkmark}>✓</span>
                  <span>{amenity}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{FALLBACKS.amenities}</p>
          )}
        </DetailCard>

        <DetailCard
          type="cancellation"
          title="Cancellation policy"
          fallback={!cancellationText}
        >
          <p>{cancellationText || FALLBACKS.cancellation}</p>
        </DetailCard>

        <DetailCard
          type="deposit"
          title="Security deposit"
          fallback={!depositText}
        >
          <p>{depositText || FALLBACKS.deposit}</p>
        </DetailCard>

        <DetailCard
          type="operator"
          title="Captain / operator"
          fallback={operatorDetails.length === 0}
        >
          {operatorDetails.length > 0 ? (
            <ul className={css.operatorList}>
              {operatorDetails.map((detail, index) => (
                <li className={css.operatorItem} key={`${detail}-${index}`}>
                  <span className={css.operatorBullet}>✓</span>
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{FALLBACKS.operator}</p>
          )}
        </DetailCard>
      </div>

      <ThrottleXVisiblePolicySummary />
      <UTVChecklistDownloadCard publicData={publicData} />
    </section>
  );
};

export default ListingPolicySections;
